import { Component } from '@theme/component';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

class PdpPromoWidget extends Component {
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener('click', this.#handleCopyClick);

    if (this.dataset.currentVariantId) {
      this.#setActiveVariant(this.dataset.currentVariantId);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('.shopify-section, dialog');

    target?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener('click', this.#handleCopyClick);

    if (this.#resetTimeout) {
      window.clearTimeout(this.#resetTimeout);
    }
  }

  #handleVariantUpdate = (event) => {
    const { productId } = event.detail.data;

    if (productId && this.dataset.productId && productId !== this.dataset.productId) return;

    const variantId = event.detail.resource?.id;

    if (!variantId) return;

    this.dataset.currentVariantId = String(variantId);
    this.#setActiveVariant(String(variantId));
  };

  #handleCopyClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-copy-button]') : null;

    if (!(button instanceof HTMLButtonElement)) return;

    const item = button.closest('[data-promo-variant]');

    if (!(item instanceof HTMLElement)) return;

    const discountCode = item.dataset.discountCode;

    if (!discountCode) return;

    this.#showCopiedState(item);

    try {
      await this.#copyText(discountCode);
    } catch (error) {
      console.error('Failed to copy promo code', error);
    }

    const applied = await this.#applyDiscountToCart(discountCode);
    if (applied) {
      // Discount is really active now, not just copied: keep the button green
      // instead of letting the timed "copied" flash revert it to red.
      this.#lockCopiedState();
    }
  };

  /**
   * Applies the discount code to the cart in the background so the customer
   * doesn't have to paste it in again at checkout.
   * @param {string} discountCode
   * @returns {Promise<boolean>} Whether the discount was actually applied.
   */
  async #applyDiscountToCart(discountCode) {
    const datoraClient = this.#getDatoraClient();
    if (!datoraClient?.setDiscountCode) return false;

    try {
      const applied = await datoraClient.setDiscountCode(discountCode);
      if (this.#isRejectedDiscountResult(applied)) return false;

      const response = await fetch('/cart.js');
      if (response.ok) {
        const cart = await response.json();

        document.dispatchEvent(
          new CartUpdateEvent(cart, this.id, {
            source: 'pdp-promo-widget',
            itemCount: cart.item_count,
          })
        );
      }

      return true;
    } catch (error) {
      console.error('Failed to apply promo discount to cart', error);
      return false;
    }
  }

  /**
   * Cancels the pending "copied" reset so the button stays in its success
   * (green) state until the customer switches to a variant without a promo.
   */
  #lockCopiedState() {
    if (this.#resetTimeout) {
      window.clearTimeout(this.#resetTimeout);
      this.#resetTimeout = undefined;
    }
  }

  /**
   * Same rejection heuristic as cart-discount.js: Datora doesn't have one
   * consistent shape for "this code didn't apply".
   * @param {unknown} result
   */
  #isRejectedDiscountResult(result) {
    if (result === false || result == null) return true;
    if (typeof result !== 'object') return false;

    const value = /** @type {Record<string, unknown>} */ (result);

    if (value.applied === false || value.success === false || value.ok === false) {
      return true;
    }

    if (typeof value.status === 'string' && ['error', 'failed', 'invalid'].includes(value.status.toLowerCase())) {
      return true;
    }

    if (Array.isArray(value.errors) && value.errors.length > 0) {
      return true;
    }

    return Boolean(value.error);
  }

  /**
   * Resolves the Datora client used to apply discount codes to the cart.
   * Same lookup as cart-discount.js: co2 isn't guaranteed to be on window.
   */
  #getDatoraClient() {
    if (window.co2 && typeof window.co2.setDiscountCode === 'function') {
      return window.co2;
    }

    try {
      return typeof co2 !== 'undefined' && co2 && typeof co2.setDiscountCode === 'function' ? co2 : null;
    } catch (error) {
      return null;
    }
  }

  #setActiveVariant(variantId) {
    const promoItems = this.querySelectorAll('[data-promo-variant]');
    let hasActivePromo = false;
    /** @type {HTMLElement | null} */
    let activeItem = null;

    for (const item of promoItems) {
      if (!(item instanceof HTMLElement)) continue;

      const isActive = item.dataset.variantId === variantId;

      item.hidden = !isActive;
      item.classList.toggle('is-active', isActive);

      if (!isActive) {
        this.#resetCopiedState(item);
      } else {
        hasActivePromo = true;
        activeItem = item;
      }
    }

    this.hidden = !hasActivePromo;

    if (activeItem) {
      this.#syncAppliedStateFromCart(activeItem);
    }
  }

  /**
   * The green "applied" look only ever lived in this page's DOM, never
   * persisted - so a fresh page load (or switching back to a variant whose
   * discount is still active from an earlier page) always starts the
   * button back at its default red state, even though the discount code
   * itself is still genuinely active in the cart. Check the real cart once
   * and restore the correct look instead of leaving it looking reset.
   * @param {HTMLElement} item
   */
  async #syncAppliedStateFromCart(item) {
    const discountCode = item.dataset.discountCode;
    if (!discountCode) return;

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;

      const cart = await response.json();
      const normalizedCode = discountCode.trim().toLowerCase();
      const isApplied =
        Array.isArray(cart.discount_codes) &&
        cart.discount_codes.some(
          (entry) => typeof entry?.code === 'string' && entry.code.trim().toLowerCase() === normalizedCode
        );

      if (isApplied) {
        this.#markAsApplied(item);
      }
    } catch (error) {
      console.error('Failed to check current cart discount state', error);
    }
  }

  async #copyText(text) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    input.style.pointerEvents = 'none';

    document.body.append(input);
    input.select();
    input.setSelectionRange(0, input.value.length);

    const copied = document.execCommand('copy');
    input.remove();

    if (!copied) {
      throw new Error('Clipboard API unavailable');
    }
  }

  #showCopiedState(item) {
    const activeItem = this.querySelector('[data-promo-variant].is-active');

    if (activeItem instanceof HTMLElement && activeItem !== item) {
      this.#resetCopiedState(activeItem);
    }

    this.#markAsApplied(item);

    if (this.refs.liveRegion instanceof HTMLElement) {
      this.refs.liveRegion.textContent = this.refs.liveRegion.dataset.successMessage || '';
    }

    if (this.#resetTimeout) {
      window.clearTimeout(this.#resetTimeout);
    }

    this.#resetTimeout = window.setTimeout(() => {
      this.#resetCopiedState(item);

      if (this.refs.liveRegion instanceof HTMLElement) {
        this.refs.liveRegion.textContent = '';
      }
    }, 2200);
  }

  /**
   * @param {HTMLElement} item
   */
  #markAsApplied(item) {
    item.setAttribute('data-copied', 'true');
    const button = item.querySelector('[data-copy-button]');
    if (button instanceof HTMLButtonElement) {
      button.setAttribute('data-copied', 'true');
    }
  }

  #resetCopiedState(item) {
    item.removeAttribute('data-copied');

    const button = item.querySelector('[data-copy-button]');
    if (button instanceof HTMLButtonElement) {
      button.removeAttribute('data-copied');
    }
  }
}

if (!customElements.get('pdp-promo-widget')) {
  customElements.define('pdp-promo-widget', PdpPromoWidget);
}
